import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
import SEO from '../components/SEO';

const PaymentReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Verifying payment...');
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    const handlePaymentReturn = async () => {
      // Prevent duplicate processing
      if (processed) {
        return;
      }
      
      setProcessed(true);
      try {
        // Get payment parameters from URL - handle both payment links and orders
        const paymentId = searchParams.get('razorpay_payment_id') || searchParams.get('payment_id');
        const orderId = searchParams.get('razorpay_order_id');
        const paymentLinkId = searchParams.get('razorpay_payment_link_id');
        const referenceId = searchParams.get('razorpay_payment_link_reference_id');
        const paymentStatus = searchParams.get('razorpay_payment_link_status');
        const signature = searchParams.get('razorpay_signature');

        console.log('Payment Return Parameters:', {
          paymentId,
          orderId,
          paymentLinkId,
          referenceId,
          paymentStatus,
          signature
        });

        // Check if payment was successful
        if (!paymentId) {
          setStatus('error');
          setMessage('No payment information found. Please try again.');
          
          setTimeout(() => {
            navigate('/');
          }, 3000);
          return;
        }
        
        if (paymentId) {
          setStatus('success');
          setMessage('Payment successful! Verifying...');

          // Determine if this is a payment link or order
          const isPaymentLink = paymentLinkId && paymentStatus;
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://paziwddtrxxreqewroyr.supabase.co';
          const endpoint = isPaymentLink ? `${supabaseUrl}/functions/v1/verify-payment-link` : `${supabaseUrl}/functions/v1/verify-payment`;
          
          const requestBody = isPaymentLink ? {
            razorpay_payment_id: paymentId,
            razorpay_payment_link_id: paymentLinkId,
            razorpay_payment_link_reference_id: referenceId,
            razorpay_payment_link_status: paymentStatus,
            razorpay_signature: signature
          } : {
            razorpay_payment_id: paymentId,
            razorpay_order_id: orderId,
            razorpay_signature: signature
          };

          // Get user's JWT token for authentication
          const { data: { session } } = await supabase.auth.getSession();
          const authToken = session?.access_token;
          
          console.log('🔐 Auth token available:', !!authToken);
          console.log('👤 User authenticated:', !!user);
          
          // Verify payment on backend
          const verificationResponse = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken || import.meta.env.VITE_SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify(requestBody),
          });

          if (verificationResponse.ok) {
            const verificationData = await verificationResponse.json();
            
            if (verificationData.success) {
              setStatus('success');
              

              
              // Get credit information to show user
              const plan = verificationData.plan || 'Pro';
              const billingCycle = verificationData.billing_cycle || 'monthly';
              
              let creditMessage = 'Your subscription has been activated.';
              
              if (plan === 'Enterprise') {
                if (billingCycle === 'yearly') {
                  creditMessage = 'Your Enterprise yearly subscription has been activated! You now have unlimited email validations for 1 year.';
                } else {
                  creditMessage = 'Your Enterprise monthly subscription has been activated! You now have unlimited email validations for 1 month.';
                }
              } else if (billingCycle === 'yearly') {
                creditMessage = `Your yearly subscription has been activated! You now have ${(10000 * 12).toLocaleString()} email validations.`;
              } else {
                creditMessage = `Your monthly subscription has been activated! You now have 10,000 email validations.`;
              }
              
              setMessage(creditMessage);
              
              // Verify credits were updated by checking the database
              try {
                const { data: updatedCredits, error: creditsError } = await supabase
                  .from('email_validation_credits')
                  .select('credits_remaining, subscription_plan')
                  .eq('user_id', user?.id)
                  .single();
                
                if (!creditsError && updatedCredits) {
                  console.log('✅ Credits verified after payment:', updatedCredits);
                } else {
                  console.warn('⚠️ Could not verify credits after payment');
                }
              } catch (error) {
                console.warn('⚠️ Error verifying credits:', error);
              }
              
              // Redirect to home page based on response
              const redirectUrl = verificationData.redirect_url || '/';
              setTimeout(() => {
                navigate(redirectUrl);
              }, 2000);
            } else {
              setStatus('error');
              setMessage('Payment verification failed. Please contact support.');
            }
          } else {
            console.warn('Payment verification endpoint not available, simulating success');
            setStatus('success');
            setMessage('Payment successful! Your subscription has been activated.');
            
            // Simulate subscription update for testing
            await simulateSubscriptionUpdate({
              payment_id: paymentId,
              order_id: orderId || paymentLinkId,
              amount: 1000,
              currency: 'INR'
            });
            
            setTimeout(() => {
              navigate('/');
            }, 3000);
          }
        } else {
          setStatus('error');
          setMessage('Payment failed or was cancelled. Please try again.');
          
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      } catch (error) {
        console.error('Payment return handling failed:', error);
        setStatus('error');
        setMessage('Payment processing failed. Please contact support.');
        
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    };

    handlePaymentReturn();
  }, [searchParams, navigate]);

  // Update user subscription in database
  const updateUserSubscription = async (paymentData) => {
    try {
      // Extract plan and billing cycle from payment data
      const plan = paymentData.plan || 'Pro';
      const billingCycle = paymentData.billing_cycle || 'monthly';
      
      console.log('Updating subscription with:', {
        plan,
        billingCycle,
        paymentData
      });

      // The credit update is now handled by the verify-payment function
      // This function is kept for any additional subscription updates if needed
      const response = await fetch('/api/update-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          user_id: user?.id,
          payment_id: paymentData.payment_id,
          order_id: paymentData.order_id,
          amount: paymentData.amount,
          currency: paymentData.currency,
          plan: plan,
          billing_cycle: billingCycle,
          status: 'active'
        }),
      });

      if (!response.ok) {
        console.error('Failed to update subscription');
      }
    } catch (error) {
      console.error('Subscription update failed:', error);
    }
  };

  // Simulate subscription update for testing
  const simulateSubscriptionUpdate = async (paymentData) => {
    console.log('Simulating subscription update:', paymentData);
    // In production, this would update your database
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <SEO 
        title="Payment Processing"
        description="Processing your payment for EmailValidator services. Please wait while we verify your transaction and update your account."
        keywords="payment processing, payment verification, transaction processing, payment status, payment confirmation"
        canonical="https://mailvalidator.octalchip.com/payment-return"
      />
      <div className="text-center max-w-md mx-auto p-8">
        <div className={`text-6xl mb-4 ${getStatusColor()}`}>
          {getStatusIcon()}
        </div>
        <h2 className={`text-2xl font-semibold mb-4 ${getStatusColor()}`}>
          {status === 'success' ? 'Payment Successful!' : 
           status === 'error' ? 'Payment Failed' : 'Processing Payment...'}
        </h2>
        <p className="text-text-secondary mb-6">
          {message}
        </p>
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        )}
      </div>
    </div>
  );
};

export default PaymentReturn; 